import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, ArrowRight, Grid3X3, AlertTriangle, X } from 'lucide-react';
import { motion } from 'framer-motion';

export function CreateWorkflowModal({ open, onOpenChange, onSelectOption, isCompanyProcess = false, linkedComplianceRequirement, onClearLinkedRequirement }) {
  // Use "Process" terminology when in company process mode
  const entityLabel = isCompanyProcess ? 'process' : 'workflow';
  const entityLabelCapitalized = isCompanyProcess ? 'Process' : 'Workflow';

  const options = [
    {
      id: 'ai-generate',
      icon: Sparkles,
      iconColor: 'text-purple-600',
      iconBg: 'bg-purple-100',
      title: 'Generate with AI',
      description: `Use Process AI to generate a custom ${entityLabel} for your use case.`,
    },
    {
      id: 'import-document',
      icon: ArrowRight,
      iconColor: 'text-blue-600',
      iconBg: 'bg-blue-100',
      title: 'Import Document',
      description: `Use Process AI to generate a custom ${entityLabel} based on your document.`,
    },
    {
      id: 'explore-templates',
      icon: Grid3X3,
      iconColor: 'text-indigo-600',
      iconBg: 'bg-indigo-100',
      title: 'Explore Templates',
      description: 'Choose from our extensive library and customize to your needs.',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle className="text-xl">Create New {entityLabelCapitalized}</DialogTitle>
        </DialogHeader>

        {/* Linked Compliance Requirement Banner */}
        {linkedComplianceRequirement && (
          <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-3">
            <div className="h-8 w-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-amber-800">Linked to Compliance Checklist</p>
              <p className="text-xs text-amber-600 truncate">{linkedComplianceRequirement.name}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-amber-600 hover:text-amber-800 hover:bg-amber-100"
              onClick={() => onClearLinkedRequirement?.()}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4 py-6">
          {options.map((option) => {
            const Icon = option.icon;
            return (
              <motion.button
                key={option.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => onSelectOption(option.id)}
                className="flex flex-col items-center p-6 border rounded-lg hover:border-primary hover:shadow-md transition-all text-center"
              >
                <div className={`p-4 rounded-lg ${option.iconBg} mb-4`}>
                  <Icon className={`h-8 w-8 ${option.iconColor}`} />
                </div>
                <h3 className="font-semibold mb-2">{option.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {option.description}
                </p>
              </motion.button>
            );
          })}
        </div>

        <div className="text-center pt-2 pb-2">
          <span className="text-muted-foreground text-sm">or </span>
          <button
            onClick={() => onSelectOption('blank')}
            className="text-primary font-medium hover:underline text-sm"
          >
            Start a Blank {entityLabelCapitalized}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
